import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import styles from "./ProfileNotifications.module.scss";
import CustomSwitch from "../../../custom/custom-switch/CustomSwitch";
import CustomButton from "../../../custom/custom-button/CustomButton";
import { useUserData } from "../../../../hooks/useUserData";
import { User } from "../../../../services";
import { toast } from "react-toastify";

const ProfileNotifications = () => {
  const { t } = useTranslation();
  const { user, refreshUserData } = useUserData();

  const [isSendEmailNotify, setIsSendEmailNotify] = useState(user.isSendEmailNotify);
  const [hasChanges, setHasChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [telegramCode, setTelegramCode] = useState(null);
  const [isSendTelegramNotify, setIsSendTelegramNotify] = useState(user.isSendTelegramNotify || false);
  const [isTelegramLoading, setIsTelegramLoading] = useState(false);

  useEffect(() => {
    if (user?.isSendTelegramNotify && user?.telegramCode) {
      setTelegramCode(user.telegramCode);
    }
  }, [user?.telegramCode, user?.isSendTelegramNotify]);

  // --- Email toggle ---
  const handleToggle = (value) => {
    setIsSendEmailNotify(value);
    setHasChanges(value !== user?.isSendEmailNotify);
  };

  const handleCancel = () => {
    setIsSendEmailNotify(user?.isSendEmailNotify || false);
    setHasChanges(false);
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await User.update({
        name: user.name,
        surname: user.surname,
        phoneNumber: user.phoneNumber,
        isSendEmailNotify: isSendEmailNotify,
      });
      await refreshUserData();
      setHasChanges(false);
      toast.success(t("profileTwo.notifications.notificationSettingsSaved"));
    } catch (error) {
      console.error("Error saving notification settings:", error);
      toast.error(t("common.genericError"));
    } finally {
      setIsLoading(false);
    }
  };

  // --- Telegram toggle ---
  const handleTelegramToggle = async (value) => {
    setIsSendTelegramNotify(value);
    setIsTelegramLoading(true);
    try {
      await User.update({
        name: user.name,
        surname: user.surname,
        phoneNumber: user.phoneNumber,
        IsSendTelegramNotify: value,
      });
      if (value) {
        let code = user?.telegramCode;
        if (!code) {
          const response = await User.generateTelegramCode();
          code = response.data.code;
        }
        setTelegramCode(code);
        if (code) {
          window.open(`https://t.me/biny2030bot?start=${code}`, "_blank", "noopener,noreferrer");
        }
      } else {
        setTelegramCode(null);
      }
    } catch (error) {
      console.error("Error toggling telegram notifications:", error);
      toast.error(t("common.genericError"));
      setIsSendTelegramNotify(!value);
    } finally {
      setIsTelegramLoading(false);
      await refreshUserData();
    }
  };

  return (
    <div className={styles.notificationsContainer}>
      <div className={styles.notificationsTitle}>{t("profileTwo.notifications.title")}</div>
      <hr className={styles.notificationsHr} />

      <div className={styles.notificationItem}>
        <div>
          <div className={cn(styles.notificationLabel)}>
            {t("profileTwo.notifications.emailNotifications")}
          </div>
          <span className={cn(styles.notificationDescription)}>
            {t("profileTwo.notifications.emailNotificationsDescription")}
          </span>
        </div>
        <CustomSwitch onToggle={handleToggle} defaultValue={isSendEmailNotify} />
      </div>

      {/* Telegram notifications */}
      <div className={styles.notificationItem}>
        <div>
          <div className={cn(styles.notificationLabel)}>
            {t("profileTwo.notifications.telegramNotifications")}
          </div>
          <span className={cn(styles.notificationDescription)}>
            {t("profileTwo.notifications.telegramNotificationsDescription")}
          </span>
          {isSendTelegramNotify && telegramCode && (
            <a
              href={`https://t.me/biny2030bot?start=${telegramCode}`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.telegramLink}
            >
              {t("profileTwo.notifications.telegramBotLinkText")} →
            </a>
          )}
        </div>
        <CustomSwitch onToggle={handleTelegramToggle} defaultValue={isSendTelegramNotify} disabled={isTelegramLoading} />
      </div>

      {hasChanges && (
        <div className={styles.buttonGroup}>
          <CustomButton
            type="button"
            variant="outlined"
            onClick={handleCancel}
            disabled={isLoading}
            text={t("common.cancel")}
            isWhiteBox
          />

          <CustomButton
            type="button"
            onClick={handleSave}
            disabled={isLoading}
            isLoading={isLoading}
            text={t("profileTwo.buttons.saveChanges")}
            isDark
          />
        </div>
      )}
    </div>
  );
};

export default ProfileNotifications;
